import React, { useState } from "react";
import {
  Card,
  Space as AntdSpace,
  Button,
  Table,
  Typography,
  Row,
  Col,
  Statistic,
  Input,
  DatePicker,
} from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import { motion } from "framer-motion";
import { useReportsServiceDownloadRevenueByUserReport, useReportsServiceGetRevenueByUserReport } from "api/queries";
import dayjs from "dayjs";

const { Title } = Typography;

const SalesByUser = () => {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [dateRange, setDateRange] = useState<[
    dayjs.Dayjs | null,
    dayjs.Dayjs | null
  ]>([
    dayjs().subtract(1, "year"),
    dayjs(),
  ]);

  const offset = (page - 1) * limit;

  const { data, isLoading } =
    useReportsServiceGetRevenueByUserReport({
      startDate: dateRange[0]?.startOf("day").toISOString(),
      endDate: dateRange[1]?.endOf("day").toISOString(),
      search,
      limit,
      offset,
    });

  const { mutate: downloadReport, isPending: isDownloadPending } =
    useReportsServiceDownloadRevenueByUserReport();

  const columns = [
    {
      title: "User Name",
      dataIndex: "userName",
      key: "userName",
    },
    {
      title: "Full Name",
      dataIndex: "userFullName",
      key: "userFullName",
    },
    {
      title: "Email",
      dataIndex: "userEmail",
      key: "userEmail",
    },
    {
      title: "Phone",
      dataIndex: "userPhone",
      key: "userPhone",
    },
    {
      title: "Total Revenue",
      dataIndex: "totalRevenue",
      key: "totalRevenue",
      render: (revenue: number) => revenue?.toLocaleString() || 0,
    },
    {
      title: "Invoice Count",
      dataIndex: "invoiceCount",
      key: "invoiceCount",
    },
  ];

  const handleTableChange = (pagination: any) => {
    if (pagination && pagination.current && pagination.pageSize) {
      setPage(pagination.current || 1);
      setLimit(pagination.pageSize || 10);
    }
  };

  const handleDownload = () => {
    downloadReport(
      {
        startDate: dateRange[0]?.startOf("day").toISOString(),
        endDate: dateRange[1]?.endOf("day").toISOString(),
        search,
      },
      {
        onSuccess: (response: any) => {
          if (response?.downloadUrl) {
            window.open(response.downloadUrl, "_blank");
          }
        },
      }
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5, delay: 0 }}
    >
      <AntdSpace direction="vertical" size="large" style={{ width: "100%" }}>
        <AntdSpace style={{ width: "100%", justifyContent: "space-between" }}>
          <Title level={2}>Sales by User</Title>
          <AntdSpace>
            <DatePicker.RangePicker
              value={dateRange}
              onChange={(dates) => {
                if (dates && dates[0] && dates[1]) {
                  setDateRange([dates[0], dates[1]]);
                  setPage(1);
                }
              }}
              style={{ marginRight: 8 }}
              allowClear={false}
            />
            <Input.Search
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Search user, email, or phone"
              style={{ width: 200 }}
            />
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              loading={isDownloadPending}
              onClick={handleDownload}
            >
              Download Report
            </Button>
          </AntdSpace>
        </AntdSpace>
        <Row gutter={16}>
          <Col span={8}>
            <Card>
              <Statistic
                title="Total Users"
                value={data?.kpis?.totalUsers || 0}
                loading={isLoading}
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card>
              <Statistic
                title="Total Revenue"
                value={data?.kpis?.totalRevenue || 0}
                loading={isLoading}
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card>
              <Statistic
                title="Total Invoices"
                value={data?.kpis?.totalInvoices || 0}
                loading={isLoading}
              />
            </Card>
          </Col>
        </Row>
        <Table
          columns={columns}
          dataSource={data?.items || []}
          pagination={{
            current: page,
            pageSize: limit,
            total: data?.total || 0,
            onChange: (page, pageSize) => {
              setPage(page);
              setLimit(pageSize);
            },
            pageSizeOptions: [10, 20, 50, 100],
            showSizeChanger: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} items`,
          }}
          loading={isLoading}
          scroll={{ x: "max-content" }}
          rowKey={(item) => item.userId}
          onChange={handleTableChange}
        />
      </AntdSpace>
    </motion.div>
  );
};

export default SalesByUser;
